import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { message, Modal, Input, Switch, DatePicker, Empty } from "antd";
import dayjs from "dayjs";
import { Search } from "lucide-react";
import {
  List_Attendance_Types,
  Add_Update_Attendance_Types,
  GET_ROLE_DETAILS,
  Role_Management,
  Get_Holidays,
  Add_Update_Holidays,
  Delete_Holiday,
} from "../../services/userAPI";
import DailyNormConfiguration from "../Dashboard/Darts/masterConfiguration/productConfiguration/dailyNormConfiguration";

/* ------------------------- shared delete modal ----------------------------- */

const DeleteConfirmModal = ({ open, title, description, loading, onCancel, onConfirm }) => (
  <Modal open={open} onCancel={onCancel} footer={null} centered title={title}>
    <p className="mt-1 text-[14px] leading-6 text-[#888888]">{description}</p>
    <div className="mt-6 flex justify-end gap-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        className="h-10 px-6 rounded-md border border-[#D9DDE3] bg-white text-[14px] font-medium text-[#6A7282] hover:bg-[#F7F8FA] transition disabled:opacity-60"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={loading}
        className="h-10 px-6 rounded-md border border-[#AD0101] bg-[#AD0101] text-[14px] font-medium text-white transition hover:bg-[#8C0101] disabled:opacity-60"
      >
        {loading ? "Deleting..." : "Delete"}
      </button>
    </div>
  </Modal>
);

/* ------------------------- Attendance & Daily Norm ------------------------- */

const AttendanceDailyNormTab = ({ roleId, accountId }) => {
  const [signInRequired, setSignInRequired] = useState(false);
  const [locationRequired, setLocationRequired] = useState(false);
  const [geoTrackingRequired, setGeoTrackingRequired] = useState(false);
  const [mandatoryTimeDistribution, setMandatoryTimeDistribution] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [signInMethod, setSignInMethod] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  /* ------------------------------ data fetch ------------------------------- */
  const { data: roleData, refetch: refetchRoleDetails } = useQuery(GET_ROLE_DETAILS, {
    variables: { jobroleId: parseInt(roleId) },
    skip: !roleId,
    onCompleted: (data) => {
      if (data.getJobrole) {
        setSignInRequired(data.getJobrole.signInSignOutRequired);
        setLocationRequired(data.getJobrole.locationRequired);
        setGeoTrackingRequired(data.getJobrole.geoTracking);
        setMandatoryTimeDistribution(data.getJobrole.mandatoryTimeDistribution);
      }
    },
  });

  const { loading, data, refetch } = useQuery(List_Attendance_Types, {
    variables: { accountId, jobRoleId: roleId },
    skip: !accountId || !roleId,
  });

  const [addUpdateAttendance, { loading: mutationLoading }] = useMutation(
    Add_Update_Attendance_Types,
    {
      onCompleted: (data) => {
        message.success(data.addUpdateAttendanceTypes.message);
        setIsModalOpen(false);
        setSignInMethod("");
        setEditingId(null);
        refetch();
      },
      onError: () => message.error("Failed to save sign-in method"),
    }
  );

  const [addJobRole, { loading: roleMutationLoading }] = useMutation(Role_Management, {
    onCompleted: (data) => {
      if (!data.addJobrole.success) message.error(data.addJobrole.message);
      refetchRoleDetails();
    },
    onError: () => {
      message.error("Failed to update role settings");
      refetchRoleDetails();
    },
  });

  /* ---------------------------- toggle handling ---------------------------- */
  const updateRoleFlag = (overrides) => {
    const currentRole = roleData?.getJobrole;
    const next = {
      signInSignOutRequired: signInRequired,
      locationRequired,
      geoTracking: geoTrackingRequired,
      mandatoryTimeDistribution,
      ...overrides,
    };

    addJobRole({
      variables: {
        accountId: parseInt(accountId),
        name: currentRole?.name || "",
        clanNeeded: currentRole?.clanNeeded ?? false,
        syncNeeded: currentRole?.syncNeeded ?? false,
        dartsNeeded: currentRole?.dartsNeeded ?? false,
        dartsFormNeeded: currentRole?.dartsFormNeeded ?? false,
        geoTracking: next.geoTracking ?? false,
        jobroleId: roleId ? parseInt(roleId) : null,
        pocName: currentRole?.pocName || "",
        pocEmail: currentRole?.pocEmail || "",
        pocNumber: currentRole?.pocContactNumber || "",
        numberLogin: currentRole?.numberLogin ?? false,
        emailLogin: currentRole?.emailLogin ?? false,
        mandatoryTimeDistribution: next.mandatoryTimeDistribution ?? false,
        signInSignOutRequired: next.signInSignOutRequired ?? false,
        locationRequired: next.locationRequired ?? false,
        targetEditDays: currentRole?.targetEditDays ?? 0,
        inputEditDays: currentRole?.inputEditDays ?? 0,
        jobroleAccessUserIds: currentRole?.jobroleAccessUsers?.map((u) => u.id) || [],
        ldmsNeeded: currentRole?.ldmsNeeded ?? false,
        mirrorNeeded: currentRole?.mirrorNeeded ?? false,
      },
    });
  };

  const handleSignInRequiredChange = (checked) => {
    setSignInRequired(checked);
    if (!checked) {
      setLocationRequired(false);
      setGeoTrackingRequired(false);
      setMandatoryTimeDistribution(false);
    }
    updateRoleFlag({
      signInSignOutRequired: checked,
      locationRequired: checked ? locationRequired : false,
      geoTracking: checked ? geoTrackingRequired : false,
      mandatoryTimeDistribution: checked ? mandatoryTimeDistribution : false,
    });
  };

  const handleLocationRequiredChange = (checked) => {
    setLocationRequired(checked);
    if (!checked) {
      setGeoTrackingRequired(false);
      setMandatoryTimeDistribution(false);
    }
    updateRoleFlag({
      locationRequired: checked,
      geoTracking: checked ? geoTrackingRequired : false,
      mandatoryTimeDistribution: checked ? mandatoryTimeDistribution : false,
    });
  };

  const handleGeoTrackingRequiredChange = (checked) => {
    setGeoTrackingRequired(checked);
    if (!checked) setMandatoryTimeDistribution(false);
    updateRoleFlag({
      geoTracking: checked,
      mandatoryTimeDistribution: checked ? mandatoryTimeDistribution : false,
    });
  };

  const handleMandatoryTimeDistributionChange = (checked) => {
    setMandatoryTimeDistribution(checked);
    updateRoleFlag({ mandatoryTimeDistribution: checked });
  };

  /* --------------------------- sign-in type CRUD --------------------------- */
  const handleSave = () => {
    setFormSubmitted(true);
    if (!signInMethod.trim()) return;
    if (!accountId || !roleId) {
      message.error("Missing account details!");
      return;
    }
    addUpdateAttendance({
      variables: {
        accountId,
        roleId,
        attendanceType: signInMethod,
        status: null,
        attendanceTypeId: editingId || null,
      },
    });
  };

  const [signInTypeToDelete, setSignInTypeToDelete] = useState(null);
  const [deletingSignInType, setDeletingSignInType] = useState(false);

  const handleDelete = (item) => setSignInTypeToDelete(item);

  const runDeleteSignInType = async () => {
    if (!signInTypeToDelete) return;
    setDeletingSignInType(true);
    try {
      await addUpdateAttendance({
        variables: {
          accountId,
          roleId,
          attendanceType: "",
          status: 0,
          attendanceTypeId: signInTypeToDelete.id,
        },
      });
      message.success("Deleted successfully!");
      refetch();
      setSignInTypeToDelete(null);
    } catch {
      message.error("Failed to delete sign-in method");
    } finally {
      setDeletingSignInType(false);
    }
  };

  const handleAddNew = () => {
    setSignInMethod("");
    setEditingId(null);
    setFormSubmitted(false);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setSignInMethod(item.attendanceType);
    setEditingId(item.id);
    setFormSubmitted(false);
    setIsModalOpen(true);
  };

  const Toggle = ({ label, checked, onChange }) => (
    <div className="flex items-center gap-2.5">
      <span className="text-[14px] text-[#1F2020]">{label}</span>
      <Switch
        checked={checked}
        onChange={onChange}
        disabled={roleMutationLoading}
        style={checked ? { backgroundColor: "#AD0101" } : undefined}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      <h3 className="text-[20px] font-semibold leading-7 text-[#1F2020]">Attendance & Daily Norm</h3>

      {/* Toggles card */}
      <div
        className="bg-white rounded-[12px] px-5 py-4 border border-[#F0F0F0] flex items-center gap-8"
        style={{ boxShadow: "0 2px 8px -2px rgba(0,0,0,0.06)" }}
      >
        <Toggle
          label="Sign-In / Sign-Out Required"
          checked={signInRequired}
          onChange={handleSignInRequiredChange}
        />
        {signInRequired && (
          <Toggle
            label="Location Required"
            checked={locationRequired}
            onChange={handleLocationRequiredChange}
          />
        )}
        {locationRequired && (
          <Toggle
            label="Geo-Tracking"
            checked={geoTrackingRequired}
            onChange={handleGeoTrackingRequiredChange}
          />
        )}
        {signInRequired && (
          <Toggle
            label="Mandatory Day-End Sign-Out"
            checked={mandatoryTimeDistribution}
            onChange={handleMandatoryTimeDistributionChange}
          />
        )}
      </div>

      {/* Sign-in method configuration */}
      {signInRequired && (
        <div
          className="bg-white rounded-[12px] p-6 border border-[#F0F0F0]"
          style={{ boxShadow: "0 2px 8px -2px rgba(0,0,0,0.06)" }}
        >
          <div className="flex items-center justify-between">
            <h4 className="text-[16px] font-semibold text-[#1F2020]">Sign-In Method Configuration</h4>
            <button
              type="button"
              onClick={handleAddNew}
              className="h-10 px-5 rounded-[6px] bg-[#AD0101] hover:bg-[#8C0101] text-white text-[14px] font-medium transition"
            >
              Add Sign-in Type
            </button>
          </div>
          <div className="mt-3">
            {loading ? (
              <div className="py-6 flex justify-center">
                <div className="h-6 w-6 border-2 border-[#AD0101] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (data?.listAttendanceTypes || []).length === 0 ? (
              <p className="py-4 text-[13px] text-[#888888] text-center">No sign-in types found</p>
            ) : (
              data.listAttendanceTypes.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between py-3 border-b border-[#F0F0F0] last:border-b-0"
                >
                  <span className="text-[14px] text-[#1F2020]">{item.attendanceType}</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleEdit(item)}
                      className="h-9 px-5 rounded-md border border-[#1F2020] bg-white text-[13px] font-medium text-[#1F2020] transition hover:bg-[#F7F8FA]"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      className="h-9 px-5 rounded-md border border-[#AD0101] bg-white text-[13px] font-medium text-[#AD0101] transition hover:bg-[#FFF2F2]"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Time distribution configuration (existing component, same APIs) */}
      <DailyNormConfiguration signInRequired={signInRequired} />

      {/* Delete sign-in type confirmation modal */}
      <DeleteConfirmModal
        open={!!signInTypeToDelete}
        title="Delete Sign-In Type"
        description={`Are you sure you want to delete the sign-in type "${signInTypeToDelete?.attendanceType || ""}"?`}
        loading={deletingSignInType}
        onCancel={() => !deletingSignInType && setSignInTypeToDelete(null)}
        onConfirm={runDeleteSignInType}
      />

      {/* Add/Edit sign-in type modal */}
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        centered
        title={editingId ? "Edit Sign-In Type" : "Add New Sign-In Type"}
      >
        <Input
          autoFocus
          value={signInMethod}
          onChange={(e) => {
            setSignInMethod(e.target.value);
            if (formSubmitted) setFormSubmitted(false);
          }}
          onPressEnter={handleSave}
          placeholder="Enter Sign-in type"
          className="h-[42px]"
        />
        {formSubmitted && !signInMethod.trim() && (
          <p className="mt-1.5 text-[12px] text-[#AD0101]">Sign-in method is required</p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setIsModalOpen(false)}
            className="h-10 px-5 rounded border border-[#D9DDE3] bg-white text-[14px] font-medium text-[#1F2020]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={mutationLoading}
            className={`h-10 px-5 rounded text-white text-[14px] font-medium transition ${mutationLoading ? "bg-[#E47777] cursor-not-allowed" : "bg-[#AD0101] hover:bg-[#8C0101]"
              }`}
          >
            {mutationLoading ? "Saving..." : editingId ? "Update" : "Save"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

/* ------------------------------ Mark Holidays ------------------------------ */

const MarkHolidaysTab = ({ accountId }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [holidayDate, setHolidayDate] = useState(null);
  const [holidayReason, setHolidayReason] = useState("");
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formErrors, setFormErrors] = useState({ date: "", reason: "" });

  const { data: holidaysData, loading: holidaysLoading, refetch } = useQuery(Get_Holidays, {
    variables: { accountId: parseInt(accountId) },
    fetchPolicy: "network-only",
    skip: !accountId,
  });
  const [addUpdateHoliday, { loading: addLoading }] = useMutation(Add_Update_Holidays);
  const [deleteHolidayMutation] = useMutation(Delete_Holiday);

  const holidays = holidaysData?.getHolidayReason || [];
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredHolidays = holidays.filter((holiday) => {
    if (!normalizedSearch) return true;
    const reason = (holiday.reason || "").toLowerCase();
    const formattedDate = dayjs(holiday.exceptionDate).format("DD/MM/YYYY").toLowerCase();
    const typeLabel = holiday.isWorking ? "working" : "holiday";
    return (
      reason.includes(normalizedSearch) ||
      formattedDate.includes(normalizedSearch) ||
      typeLabel.includes(normalizedSearch)
    );
  });

  const handleCloseModal = () => {
    setShowAddModal(false);
    setHolidayDate(null);
    setHolidayReason("");
    setEditingHoliday(null);
    setFormErrors({ date: "", reason: "" });
  };

  const handleAddHoliday = async () => {
    const errors = { date: "", reason: "" };
    if (!holidayDate) errors.date = "Date is required";
    if (!holidayReason.trim()) errors.reason = "Reason is required";
    setFormErrors(errors);
    if (errors.date || errors.reason) return;

    try {
      const response = await addUpdateHoliday({
        variables: {
          id: editingHoliday ? parseInt(editingHoliday.id) : null,
          accountId: parseInt(accountId),
          exceptionDate: dayjs(holidayDate).format("YYYY-MM-DD"),
          reason: holidayReason.trim(),
          isWorkingDay: false,
        },
      });
      if (response.data?.addUpdateHolidays?.success === false) {
        message.error(response.data.addUpdateHolidays.message || "Failed to add holiday");
        return;
      }
      message.success(editingHoliday ? "Holiday updated successfully" : "Holiday added successfully");
      handleCloseModal();
      refetch();
    } catch (error) {
      message.error(error.message || "Failed to add holiday");
    }
  };

  const handleEditHoliday = (record) => {
    setEditingHoliday(record);
    setHolidayDate(dayjs(record.exceptionDate));
    setHolidayReason(record.reason || "");
    setFormErrors({ date: "", reason: "" });
    setShowAddModal(true);
  };

  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [deletingHoliday, setDeletingHoliday] = useState(false);

  const handleDeleteHoliday = (record) => setHolidayToDelete(record);

  const runDeleteHoliday = async () => {
    if (!holidayToDelete) return;
    setDeletingHoliday(true);
    try {
      await deleteHolidayMutation({
        variables: { holidayId: parseInt(holidayToDelete.id), accountId: parseInt(accountId) },
      });
      message.success("Holiday deleted successfully");
      refetch();
      setHolidayToDelete(null);
    } catch (error) {
      message.error("Failed to delete holiday: " + error.message);
    } finally {
      setDeletingHoliday(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-[18px] font-semibold text-[#1F2020]">Mark Holidays</h2>
      <div className="flex items-center gap-4">
        <div className="flex-1 flex items-center h-[44px] rounded-[6px] border border-[#D9DDE3] px-4 bg-white">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search content..."
            className="flex-1 h-full bg-transparent outline-none text-[14px] text-[#1F2020] placeholder:text-[#737373]"
          />
          <Search className="h-4 w-4 text-[#737373] shrink-0" />
        </div>
        <button
          type="button"
          onClick={() => {
            setEditingHoliday(null);
            setHolidayDate(null);
            setHolidayReason("");
            setFormErrors({ date: "", reason: "" });
            setShowAddModal(true);
          }}
          className="h-[44px] px-5 rounded-[6px] bg-[#AD0101] hover:bg-[#8C0101] text-white text-[14px] font-medium whitespace-nowrap transition"
        >
          Add Mark Holidays
        </button>
      </div>

      <div
        className="bg-white rounded-[8px] p-4 border border-[#F0F0F0]"
        style={{ boxShadow: "0 2px 8px -2px rgba(0,0,0,0.06)" }}
      >
      <div className="grid grid-cols-[1fr_1fr_1fr_110px] gap-3 px-6 pb-4 border-b border-[#B7B6B6] text-[13px] font-semibold text-[#393939]">
        <span>S.No</span>
        <span className="text-center">Date</span>
        <span className="text-center">Reason</span>
        <span className="text-right">Actions</span>
      </div>

      {holidaysLoading ? (
        <div className="py-8 flex justify-center">
          <div className="h-6 w-6 border-2 border-[#AD0101] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredHolidays.length === 0 ? (
        <div className="py-3 px-6">
          <Empty description="No holidays found" />
        </div>
      ) : (
        filteredHolidays.map((holiday, index) => (
          <div
            key={holiday.id}
            className="grid grid-cols-[1fr_1fr_1fr_110px] gap-3 items-center px-6 py-4 border-b border-[#F0F0F0] last:border-b-0"
          >
            <span className="text-[14px] text-[#1F2020]">{index + 1}</span>
            <span className="text-[14px] text-[#1F2020] text-center">
              {dayjs(holiday.exceptionDate).format("DD/MM/YYYY")}
            </span>
            <span className="text-[14px] text-[#1F2020] text-center truncate">{holiday.reason}</span>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => handleEditHoliday(holiday)}
                className="h-8 px-4 rounded border border-[#1F2020] bg-white text-[12px] font-medium text-[#1F2020] hover:bg-[#F7F8FA] transition"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => handleDeleteHoliday(holiday)}
                className="h-8 px-4 rounded border border-[#AD0101] bg-white text-[12px] font-medium text-[#AD0101] hover:bg-[#FFF2F2] transition"
              >
                Delete
              </button>
            </div>
          </div>
        ))
      )}
      </div>

      {/* Add/Edit holiday modal */}
      <Modal
        open={showAddModal}
        onCancel={handleCloseModal}
        footer={null}
        centered
        title={editingHoliday ? "Edit Holiday" : "Add Holiday"}
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-[14px] font-medium text-[#1F2020] mb-1">
              Date <span className="text-[#AD0101]">*</span>
            </label>
            <DatePicker
              value={holidayDate}
              onChange={(d) => setHolidayDate(d)}
              format="DD/MM/YYYY"
              placeholder="Date"
              className="w-full h-[42px]"
            />
            {formErrors.date && (
              <p className="mt-1.5 text-[12px] text-[#AD0101]">{formErrors.date}</p>
            )}
          </div>
          <div>
            <label className="block text-[14px] font-medium text-[#1F2020] mb-1">
              Reason <span className="text-[#AD0101]">*</span>
            </label>
            <Input
              value={holidayReason}
              onChange={(e) => setHolidayReason(e.target.value)}
              placeholder="Enter Holiday Reason"
              className="h-[42px]"
            />
            {formErrors.reason && (
              <p className="mt-1.5 text-[12px] text-[#AD0101]">{formErrors.reason}</p>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCloseModal}
            className="h-10 px-5 rounded border border-[#1F2020] bg-white text-[14px] font-medium text-[#1F2020] hover:bg-[#F7F8FA] transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAddHoliday}
            disabled={addLoading}
            className={`h-10 px-5 rounded text-white text-[14px] font-medium transition ${addLoading ? "bg-[#E47777] cursor-not-allowed" : "bg-[#AD0101] hover:bg-[#8C0101]"
              }`}
          >
            {addLoading ? "Saving..." : editingHoliday ? "Update" : "Add"}
          </button>
        </div>
      </Modal>

      {/* Delete holiday confirmation modal */}
      <DeleteConfirmModal
        open={!!holidayToDelete}
        title="Delete Holiday"
        description={`Are you sure you want to delete the holiday on ${
          holidayToDelete ? dayjs(holidayToDelete.exceptionDate).format("DD/MM/YYYY") : ""
        }?`}
        loading={deletingHoliday}
        onCancel={() => !deletingHoliday && setHolidayToDelete(null)}
        onConfirm={runDeleteHoliday}
      />
    </div>
  );
};

/* --------------------------- Attendance section ---------------------------- */

const RoleAttendanceSection = ({ roleId, accountId }) => {
  const [subTab, setSubTab] = useState("attendance-daily-norm");

  return (
    <div>
      {/* Sub tabs */}
      <div className="flex justify-center mb-6">
        <div className="flex w-[300px] border-b border-[#D9D9D9]">
          <button
            type="button"
            onClick={() => setSubTab("attendance-daily-norm")}
            className={`flex-1 pb-3 text-[13px] border-b-2 -mb-px whitespace-nowrap transition ${subTab === "attendance-daily-norm"
                ? "border-[#AD0101] text-[#AD0101] font-semibold"
                : "border-transparent text-[#333333] font-medium"
              }`}
          >
            Attendance & Daily Norm
          </button>
          <button
            type="button"
            onClick={() => setSubTab("mark-holidays")}
            className={`flex-1 pb-3 text-[13px] border-b-2 -mb-px whitespace-nowrap transition ${subTab === "mark-holidays"
                ? "border-[#AD0101] text-[#AD0101] font-semibold"
                : "border-transparent text-[#333333] font-medium"
              }`}
          >
            Mark Holidays
          </button>
        </div>
      </div>

      {subTab === "attendance-daily-norm" ? (
        <AttendanceDailyNormTab roleId={roleId} accountId={accountId} />
      ) : (
        <MarkHolidaysTab accountId={accountId} />
      )}
    </div>
  );
};

export default RoleAttendanceSection;
